import * as DialogPrimitive from '@radix-ui/react-dialog'
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { Icon } from './icon'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogPortal = DialogPrimitive.Portal

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export type DialogMobileMode = 'sheet' | 'fullscreen'

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Layout below `sm` (640 px). `sheet` (default) docks to the bottom edge,
   * full width, up to ~92% of the viewport height, scrolling inside.
   * `fullscreen` fills the viewport — use it for long forms and editors.
   * From `sm` up the dialog is the centred modal it always was.
   */
  mobile?: DialogMobileMode
  /** Hide the corner close button (e.g. when the header renders its own). */
  hideClose?: boolean
}

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, mobile = 'sheet', hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      data-mobile={mobile}
      className={cn(
        'fixed z-50 w-full max-w-lg border-hairline bg-surface-raised p-6 shadow-[var(--shadow-popover)]',
        // sm+: centred modal (unchanged from before).
        'sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className,
        // Phones. Applied after `className` and scoped with max-sm: so the
        // desktop sizing callers pass (max-w-md, max-h-[90vh] …) can't
        // break the phone layout. The content itself scrolls so a sticky
        // <DialogFooter> stays in reach.
        'max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-w-none max-sm:overflow-y-auto max-sm:overscroll-contain',
        mobile === 'fullscreen'
          ? 'max-sm:top-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-0'
          : 'max-sm:top-auto max-sm:max-h-[92dvh] max-sm:rounded-t-2xl max-sm:border-t ah-sheet-in',
      )}
      {...props}
    >
      {mobile === 'sheet' && (
        <div
          aria-hidden
          className="sm:hidden mx-auto -mt-3 mb-3 h-1 w-10 rounded-full bg-hairline-bold"
        />
      )}
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-2 top-2 sm:right-4 sm:top-4 h-11 w-11 sm:h-auto sm:w-auto flex items-center justify-center rounded-md text-ink-500 hover:text-ink-900 transition-colors">
          <Icon name="xmark" variant="solid" className="text-base" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-left mb-4', className)}
      {...props}
    />
  )
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold text-ink-900', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-ink-500', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

/**
 * Action row for a dialog. Right-aligned on desktop; on phones it sticks to
 * the bottom of the sheet with full-width buttons, so the primary action is
 * always in thumb reach however long the form is.
 *
 * Assumes the default `p-6` content padding. If the dialog uses other
 * padding, pass matching negative offsets via className (e.g.
 * `max-sm:-mx-5 max-sm:-mb-5 max-sm:-bottom-5 max-sm:px-5` for `p-5`).
 */
export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-dialog-footer=""
      className={cn(
        'flex items-center justify-end gap-2 mt-5',
        // Sticky insets are measured from the scroll container's content box,
        // so bottom-0 would park the footer one padding (p-6) above the sheet's
        // edge with content showing beneath. -bottom-6 cancels that padding.
        'max-sm:sticky max-sm:-bottom-6 max-sm:z-10 max-sm:-mx-6 max-sm:-mb-6 max-sm:px-4 max-sm:pt-3',
        'max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]',
        'max-sm:border-t max-sm:border-hairline max-sm:bg-surface-raised',
        'max-sm:flex-col-reverse max-sm:items-stretch max-sm:[&>*]:w-full max-sm:[&>*]:min-h-11',
        className,
      )}
      {...props}
    />
  )
}
